import React, { useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';
import StyledText from '../../../components/StyledComponents/StyledText';
import { useTheme } from '../../../middleware/ThemeContext';
import { Button } from '../../../components/form/Button';
import { CorporateTheme, font, Theme } from '../../../utils/themes';
import ConnectIcon from '../../../assets/icons/ConnectIcon';
import { hexToRgba } from '../../../utils/helper';
import AddIcon from '../../../assets/icons/AddIcon';
import CloseIcon from '../../../assets/icons/CloseIcon';
import { useCustomDomainMutation, useDnsRetryMutation, useGetDnsVerificationQuery } from '../../../api/pageBuilderApiSlice';
import { useToast } from '../../../components/toast/ToastContext';
import RecordIcon from '../../../assets/icons/RecordIcon';
import NewCopyIcon from '../../../assets/icons/NewCopyIcon';
import DomainIcon from '../../../assets/icons/DomainIcon';
import NewDomainIcon from '../../../assets/icons/NewDomainIcon';
import Badge from '../../../components/Badge';
import EditIcon from '../../../assets/icons/EditIcon';
import domainBg from '../../../assets/images/domainBg.png'
import domainBgDark from '../../../assets/images/domainBG-dark.png'
import VerifyIcon from '../../../assets/icons/VerifyIcon';
import DnsIcon from '../../../assets/icons/DnsIcon';
import TickIcon from '../../../assets/icons/TickIcon';
import UserIcon from '../../../assets/icons/UserIcon';


type DomainStep = 'intro' | 'input' | 'dns' | 'connected';

type DnsRecord = {
    type: 'A' | 'CNAME';
    name: string;
    value: string;
    ttl: string;
};

interface DomainSettingsProps {
    customWebDomain?: string | null;
    cloudflareWebDomain: string;
}


const DOMAIN_REGEX = /^(?=.{1,253}$)(?!-)(?:[a-zA-Z0-9-]{1,63}\.)+[a-zA-Z]{2,63}$/;

const sanitizeDomain = (value: string) =>
    value
        .trim()
        .toLowerCase()
        .replace(/^https?:\/\//, '')
        .replace(/\/.*$/, '')
        .replace(/\.$/, '');

const DomainSettings: React.FC<DomainSettingsProps> = ({ customWebDomain, cloudflareWebDomain }) => {
    const { theme } = useTheme();
    const showToast = useToast();
    const [saveCustomDomain, { isLoading: isSavingCustomDomain }] = useCustomDomainMutation();
    const [step, setStep] = useState<DomainStep>('intro');
    const [domainInput, setDomainInput] = useState('');
    const [domainError, setDomainError] = useState('');
    const [connectedDomain, setConnectedDomain] = useState('');
    const [isEditingDomain, setIsEditingDomain] = useState(false);
    const [editingDomainInput, setEditingDomainInput] = useState('');
    const [editingDomainError, setEditingDomainError] = useState('');
    const [isCopySuccess, setIsCopySuccess] = useState(false);
    const hasCustomWebDomain = Boolean(customWebDomain && customWebDomain.trim());
    const [dnsRetry, { isLoading: isRetryingDns }] = useDnsRetryMutation();

    const { data: dnsVerificationData, isLoading: isVerifyingDns, refetch: verifyDns } = useGetDnsVerificationQuery();
    const isVerifyingDomain = isRetryingDns || isVerifyingDns;

    const RECORDS: DnsRecord[] = [
        { type: 'CNAME', name: 'WWW', value: cloudflareWebDomain, ttl: '3600' },
    ];

    const domainForDisplay = useMemo(
        () => connectedDomain || customWebDomain?.trim() || '',
        [connectedDomain, customWebDomain]
    );
    const isDomainVerified = Boolean(dnsVerificationData?.verified);

    useEffect(() => {
        const normalizedCustomDomain = sanitizeDomain(customWebDomain || '');
        if (!normalizedCustomDomain) {
            return;
        }

        setDomainInput(normalizedCustomDomain);
        setConnectedDomain(normalizedCustomDomain);
    }, [customWebDomain]);

    const handleCopy = async (value: string) => {
        try {
            await navigator.clipboard.writeText(value);
            setIsCopySuccess(true);
        } catch {
            // Ignore clipboard errors silently to keep flow smooth.
        }
    };

    useEffect(() => {
        if (!isCopySuccess) return;

        const timeoutId = window.setTimeout(() => {
            setIsCopySuccess(false);
        }, 2000);

        return () => window.clearTimeout(timeoutId);
    }, [isCopySuccess]);

    const handleGoToDns = () => {
        const normalizedDomain = sanitizeDomain(domainInput);

        if (!normalizedDomain) {
            setDomainError('Domain is required.');
            return;
        }

        if (!DOMAIN_REGEX.test(normalizedDomain)) {
            setDomainError('Please enter a valid domain (e.g. example.com).');
            return;
        }

        setDomainError('');
        setDomainInput(normalizedDomain);
        setConnectedDomain(normalizedDomain);
        setStep('dns');
    };

    const handleDnsUpdated = async () => {
        const normalizedDomain = sanitizeDomain(connectedDomain || domainInput || customWebDomain || '');

        if (!normalizedDomain) {
            showToast('Error', 'Domain is required.', 'error');
            setStep('input');
            return;
        }

        try {
            await saveCustomDomain({ data: { customWebDomain: normalizedDomain } }).unwrap();
            setDomainInput(normalizedDomain);
            setConnectedDomain(normalizedDomain);
            setStep('connected');
            showToast('Success', 'Custom domain saved successfully.', 'success');
            verifyDns();
        } catch (error: any) {
            const message = error?.data?.message || error?.error || 'Failed to save custom domain.';
            showToast('Error', message, 'error');
        }
    };

    const handleStartEditDomain = () => {
        setEditingDomainError('');
        setEditingDomainInput(isDomainVerified ? '' : (domainForDisplay || ''));
        setIsEditingDomain(true);
    };

    const handleCancelEditDomain = () => {
        setEditingDomainError('');
        setEditingDomainInput('');
        setIsEditingDomain(false);
    };

    const handleSaveEditedDomain = async () => {
        const rawDomainInput = isDomainVerified ? editingDomainInput : (editingDomainInput || domainForDisplay);
        const normalizedDomain = sanitizeDomain(rawDomainInput);

        if (!normalizedDomain) {
            setEditingDomainError('Domain is required.');
            return;
        }

        if (!DOMAIN_REGEX.test(normalizedDomain)) {
            setEditingDomainError('Please enter a valid domain (e.g. example.com).');
            return;
        }

        try {
            await saveCustomDomain({ data: { customWebDomain: normalizedDomain } }).unwrap();
            setDomainInput(normalizedDomain);
            setConnectedDomain(normalizedDomain);
            setEditingDomainError('');
            setIsEditingDomain(false);
            showToast('Success', 'Custom domain updated successfully.', 'success');
            verifyDns();
        } catch (error: any) {
            const message = error?.data?.message || error?.error || 'Failed to save custom domain.';
            setEditingDomainError(message);
            showToast('Error', message, 'error');
        }
    };

    const handleVerifyDns = async () => {
        if (isVerifyingDomain) {
            return;
        }

        try {
            await dnsRetry({}).unwrap();
            await verifyDns(); // Refetch verification status after retry
        } catch (error: any) {
            // ✅ centralized error handling
            const message =
                error?.data?.message ||
                error?.error ||
                'Something went wrong while creating website';
            showToast('Error', message, 'error');
        }
    };

    const renderEditDomainModal = () => {
        if (!isEditingDomain) {
            return null;
        }

        return (
            <EditDomainModalOverlay>
                <EditDomainModalCard theme={theme}>
                    <ModalTopBanner theme={theme}>
                        <ModalBannerContent>
                            <ModalBannerImage src={theme.name === 'light' ? domainBg : domainBgDark} alt='Domain preview' />
                            <StyledText
                                style={{
                                    position: 'absolute',
                                    top: '51px',
                                    left: '223px',
                                    maxWidth: '250px',
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    whiteSpace: 'nowrap',
                                    display: 'block'
                                }}
                                fontSize='16px'
                                fontWeight={500}
                                textColor={'#0556CB'}
                            >
                                https://{domainForDisplay}
                            </StyledText>
                        </ModalBannerContent>
                    </ModalTopBanner>

                    <ModalCloseBtn type='button' onClick={handleCancelEditDomain}>
                        <CloseIcon size={14} />
                    </ModalCloseBtn>

                    <EditModalBody>
                        <StyledText fontSize='16px' fontWeight={600} textColor={theme.CorporateTheme.colors.primary} className='text-center'>
                            Edit Primary Domain
                        </StyledText>
                        <StyledText fontSize='14px' fontWeight={500} textColor={theme.CorporateTheme.colors.textSecondary} className='text-center mt-2'>
                            Make changes in your primary domain may require DNS re-verification
                        </StyledText>

                        <EditSectionCard theme={theme} className='mt-4' isVerified={isDomainVerified}>
                            <StyledText fontSize='16px' fontWeight={600} textColor={theme.CorporateTheme.colors.primary}>Current Domain</StyledText>

                            {isDomainVerified ? (
                                <CurrentDomainReadOnly theme={theme}>
                                    <StyledText fontSize='16px' fontWeight={400} textColor={theme.CorporateTheme.colors.textSecondary}>
                                        Domain : <span style={{ color: theme.CorporateTheme.colors.primary, fontWeight: 600 }}>{domainForDisplay}</span>
                                    </StyledText>
                                    <Badge borderColor="none" padding='5px 10px' bgColor={theme.CorporateTheme.colors.count} color={"white"} fontSize='12px' fontWeight={500} text="Primary Domain" borderRadius='26px' />

                                </CurrentDomainReadOnly>
                            ) : (
                                <CurrentDomainInputWrap theme={theme}>
                                    <EditDomainInput
                                        value={editingDomainInput}
                                        onChange={(e) => {
                                            setEditingDomainInput(e.target.value);
                                            if (editingDomainError) setEditingDomainError('');
                                        }}
                                        placeholder='Enter current domain'
                                        theme={theme}
                                        fontFamily={font.raleway}
                                    />
                                    <Badge borderColor="none" padding='5px 10px' bgColor={theme.CorporateTheme.colors.count} color={"white"} fontSize='12px' fontWeight={500} text="Primary Domain" borderRadius='26px' />

                                </CurrentDomainInputWrap>
                            )}
                        </EditSectionCard>

                        {isDomainVerified ? (
                            <EditSectionCard theme={theme} className='mt-4' newDomain={true}>
                                <StyledText fontSize='16px' fontWeight={600} textColor={theme.CorporateTheme.colors.primary}>New Domain</StyledText>
                                <NewDomainInput
                                    value={editingDomainInput}
                                    onChange={(e) => {
                                        setEditingDomainInput(e.target.value);
                                        if (editingDomainError) setEditingDomainError('');
                                    }}
                                    placeholder='Enter your new domain here'
                                    theme={theme}
                                    fontFamily={font.raleway}
                                    className='mt-2'
                                />
                            </EditSectionCard>
                        ) : null}

                        <div className='mt-4'>
                            {isDomainVerified
                                ? <TipBox theme={theme} className='p-3'>
                                    <StyledText fontSize='14px' fontWeight={400} textColor={theme.CorporateTheme.colors.textSecondary} lineHeight='1.5'>
                                        <span style={{ color: theme.CorporateTheme.colors.primary, fontWeight: 600 }}>After changing the domain:</span>{' '}
                                        You will need to verify the new domain DNS records must be updated Old domain will no longer be primary
                                    </StyledText>
                                </TipBox>
                                : <TipBox theme={theme} className='p-3'>
                                    <StyledText fontSize='14px' fontWeight={400} textColor={theme.CorporateTheme.colors.primary} lineHeight='1.5'>
                                        Updating your domain without verifying the domain will not require any updating in DNS settings. Simply change the domain and hit on update button
                                    </StyledText>
                                </TipBox>}
                        </div>

                        {editingDomainError ? (
                            <StyledText className='mt-3' fontSize='13px' fontWeight={500} textColor='#d14343'>
                                {editingDomainError}
                            </StyledText>
                        ) : null}
                    </EditModalBody>

                    <EditModalFooter theme={theme}>
                        <Button
                            theme={theme}
                            type='button'
                            $variant='cancel'
                            onClick={handleCancelEditDomain}
                            disabled={isSavingCustomDomain}
                        >
                            Cancel
                        </Button>
                        <Button
                            theme={theme}
                            type='button'
                            $variant='customButton'
                            btnBg={theme.CorporateTheme.colors.primary}
                            color={theme.CorporateTheme.colors.background}
                            borderColor='none'
                            onClick={handleSaveEditedDomain}
                            disabled={isSavingCustomDomain}
                        >
                            {isSavingCustomDomain ? 'Updating...' : 'Update Domain'}
                        </Button>
                    </EditModalFooter>
                </EditDomainModalCard>
            </EditDomainModalOverlay>
        );
    };

    const renderStepsStrip = () => (
        <StepsStrip theme={theme}>
            <StepItem>
                <StepNumber>
                    <IconBadge theme={theme} size={'42px'}>
                        <DomainIcon size={22} color={theme.CorporateTheme.colors.primary} />
                    </IconBadge>
                </StepNumber>
                <StyledText className='mt-2' fontSize='14px' fontWeight={600} textColor={theme.CorporateTheme.colors.headerColor}>Enter Your Domain</StyledText>
                <StyledText className='px-5' fontSize='14px' fontWeight={400} lineHeight='1.5' textColor={theme.CorporateTheme.colors.textSecondary}>Provide the domain name you already have</StyledText>
            </StepItem>
            <StepItem>
                <StepNumber><IconBadge theme={theme} size={'42px'}>
                    <DnsIcon size={22} color={theme.CorporateTheme.colors.primary} />
                </IconBadge></StepNumber>
                <StyledText className='mt-2' fontSize='14px' fontWeight={600} textColor={theme.CorporateTheme.colors.headerColor}>Update DNS</StyledText>
                <StyledText className='px-5' fontSize='14px' fontWeight={400} lineHeight='1.5' textColor={theme.CorporateTheme.colors.textSecondary}>Add the records we provide to your registrar</StyledText>
            </StepItem>
            <StepItem>
                <StepNumber><IconBadge theme={theme} size={'42px'}>
                    <VerifyIcon size={22} color={theme.CorporateTheme.colors.primary} />
                </IconBadge></StepNumber>
                <StyledText className='mt-2' fontSize='14px' fontWeight={600} textColor={theme.CorporateTheme.colors.headerColor}>Verify Domain</StyledText>
                <StyledText className='px-5' fontSize='14px' fontWeight={400} lineHeight='1.5' textColor={theme.CorporateTheme.colors.textSecondary}>We will automatically verify and issue the SSL</StyledText>
            </StepItem>
        </StepsStrip>
    );

    const renderConnectCard = (withInput: boolean) => (
        <GradientContainer>
            <CenteredCard theme={theme}>
                <IconBadge theme={theme}>
                    <ConnectIcon size={30} color={theme.CorporateTheme.colors.primary} />
                </IconBadge>

                <TitleWrap>
                    <StyledText fontSize='18px' fontWeight={700} textColor={theme.CorporateTheme.colors.primary}>Connect Your Custom Domain</StyledText>
                    <StyledText fontSize='16px' fontWeight={400} textColor={theme.CorporateTheme.colors.textSecondary}>
                        Bought a domain on GoDaddy, Namecheap, or elsewhere? Connect it here to use it with your Site Craft website.
                    </StyledText>
                </TitleWrap>

                {!withInput ? (
                    <Button
                        theme={theme}
                        btnBg={theme.CorporateTheme.colors.primary}
                        color={theme.CorporateTheme.colors.background}
                        type='button'
                        $variant='customButton'
                        className='px-4 mx-auto'
                        borderColor='none'
                        radius='5px'
                        onClick={() => setStep('input')}
                    >
                        <AddIcon size={20} color={theme.CorporateTheme.colors.background} />
                        Connect Custom Domain
                    </Button>
                ) : (
                    <>
                        <DomainInputRow>
                            <DomainInput
                                value={domainInput}
                                onChange={(e) => {
                                    setDomainInput(e.target.value);
                                    if (domainError) setDomainError('');
                                }}
                                placeholder='eg yourdomain.com'
                                theme={theme}
                                fontFamily={font.raleway}
                            />
                            <NextButton
                                theme={theme}
                                type='button'
                                onClick={handleGoToDns}
                                disabled={!domainInput.trim() || isSavingCustomDomain}
                                fontFamily={font.raleway}
                            >
                                {isSavingCustomDomain ? 'Saving...' : 'Next'}
                            </NextButton>
                        </DomainInputRow>
                        {domainError ? (
                            <StyledText
                                className='mt-2 text-center'
                                fontSize='14px'
                                fontWeight={500}
                                textColor='#d14343'
                            >
                                {domainError}
                            </StyledText>
                        ) : null}
                        {/* <CancelLink type='button' onClick={() => setStep('intro')} fontFamily={font.raleway}>Cancel</CancelLink> */}
                    </>
                )}
            </CenteredCard>
        </GradientContainer>
    );

    const renderDnsConfig = () => (
        <GradientContainer>
            <CenteredCard theme={theme}>
                <DnsHeader className='mb-5'>
                    <div>
                        <StyledText fontSize='22px' fontWeight={700} textColor={theme.CorporateTheme.colors.primary}>
                            Configure DNS for "{connectedDomain || domainInput || customWebDomain}"
                        </StyledText>
                        <StyledText className='mt-3' fontSize='16px' fontWeight={500} textColor={theme.CorporateTheme.colors.textSecondary}>
                            Follow the following steps in your domain dns dashboard to connect a domain
                        </StyledText>
                    </div>
                    {/* <CloseIcon size={18} onClick={() => setStep('input')} className='cursor-pointer' /> */}
                </DnsHeader>

                <InstructionBlock className='mb-5 pb-2'>
                    {/* <StepNumber dark>1</StepNumber> */}
                    <DomainIconWrap theme={theme}><UserIcon size={28} color={theme.CorporateTheme.colors.primary} /></DomainIconWrap>
                    <div>
                        <StyledText fontSize='18px' fontWeight={600} textColor={theme.CorporateTheme.colors.primary}>Log in to Domain DNS</StyledText>
                        <StyledText fontSize='16px' fontWeight={500} textColor={theme.CorporateTheme.colors.textSecondary} className="mt-2">Go to your Domain Portfolio and select your domain.</StyledText>
                    </div>
                </InstructionBlock>


                <InstructionBlock>
                    {/* <StepNumber dark>2</StepNumber> */}
                    <DomainIconWrap theme={theme}><RecordIcon size={28} color={theme.CorporateTheme.colors.primary} /></DomainIconWrap>
                    <div>
                        <StyledText fontSize='18px' fontWeight={600} textColor={theme.CorporateTheme.colors.primary}>Add A Record</StyledText>
                        <StyledText fontSize='16px' fontWeight={500} textColor={theme.CorporateTheme.colors.textSecondary} className="mt-2">In the DNS Management section, add a new A record with these values:</StyledText>
                    </div>
                </InstructionBlock>

                <RecordCard theme={theme} className='mt-5 d-flex'>
                    <RecordTable className='me-5'>
                        <colgroup>
                            <col style={{ width: '13.33%' }} />
                            <col style={{ width: '13.33%' }} />
                            <col style={{ width: '33.33%' }} />
                        </colgroup>
                        <thead>
                            <tr>
                                <th scope="col"><StyledText fontSize='16px' fontWeight={600} textColor={theme.CorporateTheme.colors.primary}>Type</StyledText></th>
                                <th scope="col"><StyledText fontSize='16px' fontWeight={600} textColor={theme.CorporateTheme.colors.primary}>Name</StyledText></th>
                                <th scope="col"><StyledText fontSize='16px' fontWeight={600} textColor={theme.CorporateTheme.colors.primary}>Value</StyledText></th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr>
                                <td><StyledText fontSize='16px' fontWeight={400} textColor={theme.CorporateTheme.colors.textSecondary}>CNAME</StyledText></td>
                                <td><StyledText fontSize='16px' fontWeight={400} textColor={theme.CorporateTheme.colors.textSecondary}>www</StyledText></td>
                                <td><StyledText fontSize='16px' fontWeight={400} textColor={theme.CorporateTheme.colors.textSecondary}>{cloudflareWebDomain}</StyledText></td>
                            </tr>
                        </tbody>

                    </RecordTable>
                    <CopyBtn theme={theme} onClick={() => handleCopy(cloudflareWebDomain)} className='me-5'>
                        <StyledText fontWeight={500} textColor={theme.CorporateTheme.colors.background} fontSize='14px'>
                            {isCopySuccess ? 'Copied' : 'Copy Value'}
                        </StyledText>
                        {isCopySuccess ? (
                            <TickIcon size={18} color={theme.CorporateTheme.colors.background} className='ms-3' />
                        ) : (
                            <NewCopyIcon color={theme.CorporateTheme.colors.background} className='ms-3' />
                        )}
                    </CopyBtn>

                </RecordCard>

                <TipBox theme={theme}>
                    <StyledText fontSize='16px' fontWeight={500} textColor={theme.CorporateTheme.colors.textSecondary} lineHeight='1.5'>
                        <span style={{ color: theme.CorporateTheme.colors.primary, fontWeight: 600 }}>Domain DNS Tip:</span>{' '}
                        If you see an existing A record with the name "@", edit it instead of creating a new one. GoDaddy doesn't allow multiple A records for the same name.
                    </StyledText>
                </TipBox>

                <DnsButtons>
                    <Button
                        theme={theme}
                        btnBg={theme.CorporateTheme.colors.primary}
                        color={theme.CorporateTheme.colors.background}
                        type='button'
                        $variant='customButton'
                        className='px-4'
                        borderColor='none'
                        width='100%'
                        onClick={handleDnsUpdated}
                        disabled={isSavingCustomDomain}
                    >
                        {isSavingCustomDomain ? 'Saving...' : "I've updated my DNS records"}
                    </Button>
                    {/* <Button
                    theme={theme}
                    btnBg={theme.CorporateTheme.colors.filterBG}
                    color={theme.CorporateTheme.colors.textSecondary}
                    type='button'
                    $variant='customButton'
                    className='px-4'
                    borderColor={theme.CreateFormTheme.colors.inputBorders}
                    onClick={() => setStep('connected')}
                >
                    Next
                </Button> */}
                </DnsButtons>
            </CenteredCard>
        </GradientContainer>
    );

    const renderConnected = () => (
        <>
            <GradientContainer>
                <CenteredCard theme={theme}>
                    <ConnectedCard theme={theme}>
                        <PrimaryRow theme={theme}>
                            <div className='d-flex align-items-center gap-3'>
                                <DomainIconWrap theme={theme} bgColor={theme.CorporateTheme.colors.background}><NewDomainIcon size={28} color={theme.CorporateTheme.colors.primary} /></DomainIconWrap>
                                <div>
                                    <div className='d-flex align-items-center gap-2'>
                                        <StyledText fontSize='18px' fontWeight={700} textColor={theme.CorporateTheme.colors.primary}>{domainForDisplay}</StyledText>
                                        <Badge borderColor="none" padding='5px 10px' bgColor={theme.CorporateTheme.colors.count} color={"white"} fontSize='12px' fontWeight={600} text="Primary Domain" borderRadius='26px' />
                                    </div>
                                    <>
                                        <StyledText fontSize='16px' fontWeight={500} textColor={theme.CorporateTheme.colors.textSecondary} className='mt-2'>
                                            {dnsVerificationData?.verified
                                                ? `Your site is currently live at ${domainForDisplay}`
                                                : `Your domain is not verify yet. Please verify your DNS records`}
                                        </StyledText>

                                    </>
                                </div>
                            </div>
                            {dnsVerificationData?.verified ? (
                                <div className='d-flex align-items-center gap-2'>
                                    <EditLinkBtn
                                        type='button'
                                        onClick={handleStartEditDomain}
                                        disabled={isVerifyingDomain || isSavingCustomDomain}
                                        theme={theme}
                                    >
                                        <EditIcon color={theme.CorporateTheme.colors.headerColor} />
                                    </EditLinkBtn>
                                    {/* <ConnectedPill theme={theme}>Connected</ConnectedPill> */}
                                    <Badge borderColor="none" padding='10px 15px' bgColor={'#009828'} color={"white"} fontSize='14px' fontWeight={600} text="Connected" borderRadius='26px' />

                                </div>
                            ) : (
                                <div className='d-flex align-items-center gap-2'>
                                    <EditLinkBtn
                                        type='button'
                                        onClick={handleStartEditDomain}
                                        disabled={isVerifyingDomain || isSavingCustomDomain}
                                        theme={theme}
                                    >
                                        <EditIcon color={theme.CorporateTheme.colors.headerColor} />
                                    </EditLinkBtn>
                                    <Button
                                        type='button'
                                        $variant='customButton'
                                        onClick={handleVerifyDns}
                                        disabled={isVerifyingDomain || isSavingCustomDomain}
                                        theme={theme}
                                        btnBg={theme.CorporateTheme.colors.blueberry}
                                        color={theme.CorporateTheme.colors.background}
                                        borderColor='none'
                                    >
                                        {isVerifyingDomain ? 'Verifying...' : 'Verify'}
                                    </Button>
                                </div>
                            )}
                        </PrimaryRow>

                        <SectionCard theme={theme}>
                            <DnsTop>
                                <div>
                                    <StyledText fontSize='18px' fontWeight={700} textColor={theme.CorporateTheme.colors.primary}>DNS Management</StyledText>
                                    <StyledText fontSize='16px' fontWeight={500} textColor={theme.CorporateTheme.colors.textSecondary} className='mt-2'>Configure your domain's DNS records to point to our servers.</StyledText>
                                </div>
                                {/* <Button
                            theme={theme}
                            btnBg={theme.CorporateTheme.colors.primary}
                            color={theme.CorporateTheme.colors.background}
                            type='button'
                            $variant='customButton'
                            className='px-4'
                            borderColor='none'
                        >
                            Add Records
                        </Button> */}
                            </DnsTop>

                            <ManageTable theme={theme}>
                                <thead>
                                    <tr>
                                        <th><StyledText fontSize='15px' fontWeight={600} textColor={theme.CorporateTheme.colors.primary}>Type</StyledText></th>
                                        <th><StyledText fontSize='15px' fontWeight={600} textColor={theme.CorporateTheme.colors.primary} className='text-center'>Name</StyledText></th>
                                        <th><StyledText fontSize='15px' fontWeight={600} textColor={theme.CorporateTheme.colors.primary} className='text-center'>Value / Target</StyledText></th>
                                        <th><StyledText fontSize='15px' fontWeight={600} textColor={theme.CorporateTheme.colors.primary} className='text-center'>TTL</StyledText></th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {RECORDS.map((record) => (
                                        <tr key={`${record.type}-${record.name}`}>
                                            <td><StyledText fontSize='16px' fontWeight={400} textColor={theme.CorporateTheme.colors.textSecondary}>{record.type}</StyledText></td>
                                            <td><StyledText fontSize='16px' fontWeight={400} textColor={theme.CorporateTheme.colors.textSecondary} className='text-center'>{record.name}</StyledText></td>
                                            <td><StyledText fontSize='16px' fontWeight={400} textColor={theme.CorporateTheme.colors.textSecondary} className='text-center'>{record.value}</StyledText></td>
                                            <td><StyledText fontSize='16px' fontWeight={400} textColor={theme.CorporateTheme.colors.textSecondary} className='text-center'>{record.ttl}</StyledText></td>
                                        </tr>
                                    ))}
                                </tbody>
                            </ManageTable>
                        </SectionCard>

                        {/* <NameServerCard theme={theme}>
                    <NsHeader>
                        <DomainIconWrap theme={theme}><DomainIcon size={16} color={theme.CorporateTheme.colors.primary} /></DomainIconWrap>
                        <div>
                            <StyledText fontSize='26px' fontWeight={700} textColor={theme.CorporateTheme.colors.primary}>Nameservers</StyledText>
                            <StyledText fontSize='15px' fontWeight={500} textColor={theme.CorporateTheme.colors.textSecondary}>Point your domain to our nameservers</StyledText>
                        </div>
                    </NsHeader>

                    <NsRow theme={theme}>
                        <span>ns1.sitecraft.com</span>
                        <CopyBtn type='button' onClick={() => handleCopy('ns1.sitecraft.com')}>Copy</CopyBtn>
                    </NsRow>
                    <NsRow theme={theme}>
                        <span>ns2.sitecraft.com</span>
                        <CopyBtn type='button' onClick={() => handleCopy('ns2.sitecraft.com')}>Copy</CopyBtn>
                    </NsRow>
                </NameServerCard> */}
                    </ConnectedCard>
                </CenteredCard>
            </GradientContainer>
            {renderEditDomainModal()}
        </>
    );

    if (hasCustomWebDomain) {
        return <Outer theme={theme}>{renderConnected()}</Outer>;
    }

    return (
        <Outer theme={theme}>
            {step === 'intro' && (
                <>
                    {renderConnectCard(false)}
                    {renderStepsStrip()}
                </>
            )}

            {step === 'input' && (
                <>
                    {renderConnectCard(true)}
                    {renderStepsStrip()}
                </>
            )}

            {step === 'dns' && renderDnsConfig()}

            {step === 'connected' && renderConnected()}
        </Outer>
    );
};

const Outer = styled.div<{ theme: Theme }>`
  border: 1px solid ${({ theme }) => theme.CorporateTheme.colors.border};
  background: ${({ theme }) => theme.CorporateTheme.colors.background};
  border-radius: 10px;
  padding: 48px 36px;
  min-height: 720px;
  display: flex;
  flex-direction: column;
  align-items: center;
//   gap: 22px;
`;

const GradientContainer = styled.div`  
 background: linear-gradient(180deg, #b8d4f7 0%, #d8e7fb 45%, rgba(216, 231, 251, 0) 100%);
border-radius: 10px 10px 0 0;

  width: min(1120px, 100%);
 padding: 1px;
 `;

const CenteredCard = styled.div<{ theme: Theme }>`
background: ${({ theme }) => theme.CorporateTheme.colors.background};
border-radius: 10px 10px 0 0;
padding: 44px 36px;
//   width: min(1120px, 100%);
//     position: relative;
//     border: none;
//     overflow: visible;
//     background: ${({ theme }) => theme.CorporateTheme.colors.background};
//   padding: 44px 36px;
//     border-radius: 10px 10px 0 0;

//     &::before {
//         content: '';
//         position: absolute;
//         inset: 0;
//         pointer-events: none;
//         border-radius: inherit;
//         background: linear-gradient(90deg, #b8d4f7 0%, #d8e7fb 50%, #b8d4f7 100%) top/100% 1px no-repeat;
//     }

//     &::after {
//         content: '';
//         position: absolute;
//         inset: 0;
//         pointer-events: none;
//         border-radius: inherit;
//         background:
//             linear-gradient(180deg, #b8d4f7 0%, #d8e7fb 55%, rgba(216, 231, 251, 0) 100%) left/1px 100% no-repeat,
//             linear-gradient(180deg, #b8d4f7 0%, #d8e7fb 55%, rgba(216, 231, 251, 0) 100%) right/1px 100% no-repeat;
//     }
`;

const IconBadge = styled.div<{ theme: Theme, size?: string }>`
  width: ${props => props.size || '60px'};
  height: ${props => props.size || '60px'};
  border-radius: 10px;
  background: ${({ theme }) => theme.CorporateTheme.colors.lightBlueBg};
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto;
`;

const TitleWrap = styled.div`
  max-width: 760px;
  margin: 18px auto 26px;
  text-align: center;
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const DomainInputRow = styled.div`
  width: min(660px, 100%);
  margin: 0 auto;
  display: grid;
  grid-template-columns: 1fr 110px;
`;



const DomainInput = styled.input<{ theme: Theme, fontFamily: string }>`
  height: 49px;
  border: 1px solid ${({ theme }) => hexToRgba(theme.CorporateTheme.colors.primary, 0.05)};
  border-right: none;
  border-radius: 8px 0 0 8px;
  padding: 0 14px;
  font-size: 14px;
  font-family: ${props => props.fontFamily};
  color: ${({ theme }) => theme.CorporateTheme.colors.headerColor};
  background: ${({ theme }) => hexToRgba(theme.CorporateTheme.colors.primary, 0.05)};
  outline: none;
`;

const NextButton = styled.button<{ theme: Theme, fontFamily: string }>`
  height: 49px;
  border: none;
  border-radius: 0 8px 8px 0;
  font-size: 14px;
  font-weight: 700;
  font-family: ${props => props.fontFamily};
  color: ${({ theme }) => theme.CorporateTheme.colors.background};
  background: ${({ theme }) => theme.CorporateTheme.colors.primary};
  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

const CancelLink = styled.button<{ fontFamily: string }>`
  margin: 12px auto 0;
  display: block;
  border: none;
  background: transparent;
  font-size: 16px;
  color: #6b7280;
   font-family: ${props => props.fontFamily};
`;

const StepsStrip = styled.div<{ theme: Theme }>`
  width: min(1120px, 100%);
  background: ${({ theme }) => theme.CorporateTheme.colors.lightOrangeBG};
  border: 1px solid #f4d7cc;
  border-bottom-left-radius: 20px;
  border-bottom-right-radius: 20px;
  padding: 28px 24px;
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 14px;
`;

const StepItem = styled.div`
  text-align: center;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
`;

const StepNumber = styled.div<{ dark?: boolean }>`
  width: 75px;
  height: 75px;
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 18px;
  font-weight: 700;
  color: ${({ dark }) => (dark ? '#ffffff' : '#5f6368')};
  background: ${({ dark }) => (dark ? '#0d3d77' : '#ffffff')};
`;

const StepDesc = styled.p`
  margin: 0;
  font-size: 16px;
  color: #6b7280;
  max-width: 260px;
`;

const DnsHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 16px;
  margin-bottom: 18px;
`;

const CloseBtn = styled.button`
  border: none;
  background: transparent;
  font-size: 22px;
  color: #6b7280;
`;

const InstructionBlock = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 14px;
  margin-top: 14px;
`;

const RecordCard = styled.div<{ theme: Theme }>`
  border: 1px solid ${({ theme }) => hexToRgba(theme.CorporateTheme.colors.blueberry, 0.09)};
  background: ${({ theme }) => hexToRgba(theme.CorporateTheme.colors.blueberry, 0.07)};
  border-radius: 6px;
  margin: 8px 0 16px;
  padding: 20px 12px;
`;

const RecordTable = styled.table`
  width: 100%;
    table-layout: fixed;
  text-align: center;

  th {
    text-align: center;
    font-size: 16px;
    color: #6b7280;
    font-weight: 400;
    padding: 6px 8px;
  }

  td {
    font-size: 14px;
    color: #1f2937;
    padding: 6px 8px;
  }
`;

const CopyBtn = styled.div<{ theme: Theme }>`
    display: flex;
  border: none;
  background: ${({ theme }) => theme.CorporateTheme.colors.count};
  color: #0d3d77;
  font-size: 13px;
  font-weight: 700;
  height: 44px;
  width: 160px;
  align-self: center;
    justify-content: center;
    align-items: center;
  border-radius:3px;
  gap: 6px;
  cursor: pointer;
`;

const TipBox = styled.div<{ theme: Theme }>`
  margin-top: 6px;
  border: 1px solid #f3d4c8;
  background: ${({ theme }) => theme.CorporateTheme.colors.lightOrangeBG};
  border-radius: 10px;
  color: #6b7280;
  font-size: 14px;
  padding: 12px 14px;
`;

const DnsButtons = styled.div`
  margin-top: 20px;
  display: flex;
  gap: 10px;
`;

const ConnectedCard = styled.div<{ theme: Theme }>`
  border-radius: 10px;
`;

const PrimaryRow = styled.div<{ theme: Theme }>`
  border: 1px solid ${({ theme }) => hexToRgba(theme.CorporateTheme.colors.blueberry, 0.2)};
  border-radius: 10px;
  background: ${({ theme }) => hexToRgba(theme.CorporateTheme.colors.blueberry, 0.05)};
  padding: 40px;
  display: flex;
  align-items: center;
  justify-content: space-between;
    gap: 12px;
`;

const DomainIconWrap = styled.div<{ theme: Theme, bgColor?: string }>`
  width: 56px;
  height: 56px;
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: ${({ theme, bgColor }) => bgColor || hexToRgba(theme.CorporateTheme.colors.blueberry, 0.07)};
`;

const ConnectedPill = styled.span<{ theme: Theme }>`
  margin-left: auto;
  border-radius: 16px;
  padding: 6px 12px;
  font-size: 12px;
  color: #7d5818;
  background: #fff3de;
  border: 1px solid #f3d9a9;
`;

const EditLinkBtn = styled.button<{ theme: Theme }>`
display: flex;
align-items: center;
justify-content: center;
    border: none;
    height: 42px;
    width:42px;
    background: ${({ theme }) => theme.CorporateTheme.colors.background};
    padding: 0;
    color: #0d3d77;
    font-size: 14px;
    font-weight: 600;
    text-decoration: underline;
    border-radius:49px;
    &:disabled {
        opacity: 0.6;
        cursor: not-allowed;
    }
`;

const EditDomainInput = styled.input<{ theme: Theme, fontFamily: string }>`
    height: 42px;
    border: none;
    border-radius: 8px;
    padding: 0 12px;
    font-size: 15px;
    font-family: ${props => props.fontFamily};
    color: ${({ theme }) => theme.CorporateTheme.colors.primary};
    background: ${({ theme }) => theme.CorporateTheme.colors.background};
    font-weight: 600;
    outline: none;
    width: 75%;
`;

const EditDomainModalOverlay = styled.div`
    position: fixed;
    inset: 0;
    background: rgba(0, 0, 0, 0.45);
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 1000;
    padding: 20px;
`;

const EditDomainModalCard = styled.div<{ theme: Theme }>`
    width: min(700px, 100%);
    background: ${({ theme }) => theme.CorporateTheme.colors.background};
    border-radius: 8px;
    position: relative;
    overflow: hidden;
    padding:5px;
`;

const ModalTopBanner = styled.div<{ theme: Theme }>`
    background: ${({ theme }) => hexToRgba(theme.CorporateTheme.colors.blueberry, 0.15)};
    padding: 0px 20px;
    display: flex;
    justify-content: center;
    padding-top:30px;
    border-radius:8px;
`;

const ModalBannerContent = styled.div`
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 10px;
    position: relative;
`;

const ModalBannerImage = styled.img`
    width: min(350px, 100%);
    height: auto;
    display: block;
`;

const BrowserPreviewCard = styled.div`
    width: min(350px, 100%);
    background: #f6f6f6;
    border: 1px solid #e3e8f2;
    border-radius: 14px;
    padding: 16px;
    position: relative;
    z-index: 1;
    margin-bottom: 16px;

    box-shadow: 0 12px 22px rgba(31, 53, 94, 0.12);

    &::after {
        content: "";
        position: absolute;

        top: 10px;
        left: 10px;

        width: 100%;
        height: 100%;

        background: gray; /* 👈 very light gray (NOT dark) */
        border-radius: 14px;

        z-index: -99;

        /* very soft shadow */
        box-shadow: 0 8px 16px rgba(0, 0, 0, 0.05);

        opacity: 1; /* 👈 don't fade it too much */
    }
`;

const BrowserDots = styled.div`
    display: flex;
    gap: 8px;
    margin-bottom: 10px;

    span {
        width: 10px;
        height: 10px;
        border-radius: 50%;
    }

    span:nth-child(1) {
        background: #ff5f57;
    }

    span:nth-child(2) {
        background: #febc2e;
    }

    span:nth-child(3) {
        background: #28c840;
    }
`;

const BrowserUrlRow = styled.div`
    min-height: 44px;
    border-radius: 8px;
    background: #e9e9e9;
    display: flex;
    align-items: center;
    gap: 10px;
    padding: 0 12px;
`;

const BrowserLockIcon = styled.span`
    width: 14px;
    height: 14px;
    border-radius: 2px;
    background: #111827;
    position: relative;

    &::before {
        content: '';
        position: absolute;
        top: -6px;
        left: 2px;
        width: 10px;
        height: 8px;
        border: 2px solid #111827;
        border-bottom: 0;
        border-radius: 8px 8px 0 0;
        box-sizing: border-box;
    }
`;

const ModalCloseBtn = styled.button`
    position: absolute;
    top: 14px;
    right: 14px;
    width: 28px;
    height: 28px;
    border-radius: 50%;
    border: none;
    background: #f3f4f6;
    display: flex;
    align-items: center;
    justify-content: center;
`;

const EditModalBody = styled.div`
    padding: 28px 34px 20px;
`;

const EditSectionCard = styled.div<{ theme: Theme, newDomain?: boolean,isVerified?: boolean }>`
    border: 1px solid ${({ theme, newDomain,isVerified }) => newDomain  || !isVerified? hexToRgba(theme.CorporateTheme.colors.blueberry, 0.15) : theme.CorporateTheme.colors.border};
    background: ${({ theme, newDomain,isVerified }) => newDomain || !isVerified ? hexToRgba(theme.CorporateTheme.colors.blueberry, 0.05) : theme.CorporateTheme.colors.filterBG};
    border-radius: 10px;
    padding: 18px 20px;
`;

const CurrentDomainInputWrap = styled.div<{ theme: Theme }>`
    margin-top: 10px;
    border-radius: 8px;
    background: ${({ theme }) => theme.CorporateTheme.colors.background};
    border: 1px solid ${({ theme }) => theme.CorporateTheme.colors.border};
    padding: 6px;
    display: flex;
    align-items: center;
    gap: 8px;
`;

const CurrentDomainReadOnly = styled.div<{ theme: Theme }>`
    margin-top: 10px;
    border-radius: 8px;
    background: ${({ theme }) => theme.CorporateTheme.colors.background};
    border: 1px solid ${({ theme }) => theme.CorporateTheme.colors.border};
    min-height: 48px;
    padding: 8px 12px;
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 10px;
`;

const PrimaryDomainPill = styled.span`
    border-radius: 12px;
    background: #ff7f50;
    color: #ffffff;
    font-size: 11px;
    padding: 4px 10px;
    font-weight: 600;
    white-space: nowrap;
`;

const NewDomainInput = styled.input<{ theme: Theme, fontFamily: string }>`
    width: 100%;
    min-height: 46px;
    border: 1px solid ${({ theme }) => theme.CorporateTheme.colors.border};
    border-radius: 8px;
    background: ${({ theme }) => theme.CorporateTheme.colors.background};
    padding: 0 12px;
    outline: none;
    font-family: ${props => props.fontFamily};
    color: ${({ theme }) => theme.CorporateTheme.colors.headerColor};
`;

const InfoBanner = styled.div<{ theme: Theme, isWarning?: boolean }>`
    border-radius: 8px;
    padding: 16px 18px;
    border: 1px solid ${({ isWarning }) => (isWarning ? '#f0d1c7' : '#d3deef')};
    background: ${({ theme, isWarning }) => (isWarning ? theme.CorporateTheme.colors.lightOrangeBG : hexToRgba(theme.CorporateTheme.colors.blueberry, 0.05))};
`;

const EditModalFooter = styled.div<{ theme: Theme }>`
    border-top: 1px solid ${({ theme }) => theme.CorporateTheme.colors.border};
    padding: 14px 24px;
    display: flex;
    justify-content: flex-end;
    gap: 10px;
`;

const VerifyBtn = styled.button<{ theme: Theme }>`
    margin-left: auto;
    border-radius: 8px;
    padding: 8px 14px;
    border: none;
    background: ${({ theme }) => theme.CorporateTheme.colors.primary};
    color: ${({ theme }) => theme.CorporateTheme.colors.background};
    font-size: 13px;
    font-weight: 700;

    &:disabled {
        opacity: 0.6;
        cursor: not-allowed;
    }
`;

const SectionCard = styled.div<{ theme: Theme }>`
  border: 1px solid ${({ theme }) => theme.CorporateTheme.colors.lightOrangeBG};
  background: ${({ theme }) => theme.CorporateTheme.colors.lightOrangeBG};
  border-radius: 10px;
  padding: 40px;
  margin-top: 14px;
`;

const DnsTop = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
//   padding:40px;
margin-bottom: 30px;
`;

const ManageTable = styled.table<{ theme: Theme }>`
  width: 100%;
  border-collapse: collapse;
  background:  ${({ theme }) => theme.CorporateTheme.colors.background};
    border-radius:8px;
  th {
    text-align: left;
    font-size: 13px;
    font-weight: 600;
    color: #6b7280;
    // background: ${({ theme }) => theme.CorporateTheme.colors.filterBG};
    padding: 10px 40px;
    padding-top:20px;
  }

  td {
    font-size: 14px;
    color: #374151;
    padding: 0px 40px;
    padding-bottom: 20px;
    // border-top: 1px solid #e5e7eb;
  }
`;

const ActionBtn = styled.button`
  border: none;
  background: transparent;
  color: #0d3d77;
  font-size: 13px;
  font-weight: 700;
`;

const NameServerCard = styled.div<{ theme: Theme }>`
  width: min(430px, 100%);
  border: 1px solid ${({ theme }) => theme.CorporateTheme.colors.border};
  border-radius: 10px;
  padding: 14px;
  margin-top: 14px;
`;

const NsHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 12px;
`;

const NsRow = styled.div<{ theme: Theme }>`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 10px 12px;
  border-radius: 8px;
  background: ${({ theme }) => theme.CorporateTheme.colors.filterBG};
  margin-top: 8px;
`;

export default DomainSettings;
